import React, { useEffect } from 'react';
import { tutorApi } from '../../tutorApi';

function ChatMessages({ 
    selectedChat, 
    messages, 
    loadingMessages, 
    messageError, 
    newMessage, 
    sendingMessage,
    onMessageChange,
    onKeyDown,
    onSendMessage,
    hasMoreMessages,
    loadingMoreMessages,
    onLoadMore
}) {
    // Use real messages data from API
    const currentMessages = messages || [];
    
    useEffect(() => {
        if (selectedChat && currentMessages.length > 0) {
            const hasUnreadMessages = currentMessages.some(
                message => !message.isRead && message.sender !== 'tutor'
            );
            
            if (hasUnreadMessages) {
                tutorApi.markMessagesAsRead(selectedChat.id)
                    .catch(error => console.error('Error marking messages as read:', error));
            }
        }
    }, [selectedChat, currentMessages]);

    return (
        <div className="h-full flex flex-col bg-white">
            {selectedChat ? (
                <>
                    {/* Header */}
                    <div className="px-6 py-6 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                        <div className="flex flex-col">
                            <h3 className="text-lg font-semibold text-gray-800 mb-1">
                                {selectedChat.name}
                            </h3>
                            <div className="flex items-center gap-2">
                                <p className="text-sm text-gray-500">
                                    {selectedChat.role}
                                </p>
                                {selectedChat.timezone && selectedChat.role === 'Student' && (
                                    <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                        {selectedChat.timezone}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-6 flex flex-col-reverse min-h-0">
                        <div className="space-y-5">
                            {/* Load More Button */}
                            {hasMoreMessages && (
                                <div className="flex justify-center pb-4">
                                    <button
                                        onClick={onLoadMore}
                                        disabled={loadingMoreMessages}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                            loadingMoreMessages
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200 hover:shadow-md'
                                        }`}
                                    >
                                        {loadingMoreMessages ? (
                                            <span className="flex items-center gap-2">
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Loading older messages...
                                            </span>
                                        ) : (
                                            'Load Older Messages'
                                        )}
                                    </button>
                                </div>
                            )}
                            
                            {loadingMessages ? (
                                <div className="text-center text-gray-600 py-8">Loading messages...</div>
                            ) : messageError ? (
                                <div className="text-center text-red-600 py-8">{messageError}</div>
                            ) : currentMessages.length === 0 ? (
                                <div className="text-center text-gray-600 py-8">No messages yet</div>
                            ) : (
                                currentMessages.map((message) => (
                                    <div 
                                        key={message.id}
                                        className={`flex ${message.sender === 'tutor' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div 
                                            className={`max-w-xs lg:max-w-md px-5 py-3 rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
                                                message.sender === 'tutor' 
                                                    ? 'bg-blue-500 text-white' 
                                                    : message.sender === 'staff'
                                                    ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}
                                        >
                                            {/* Sender Name and Role */}
                                            <div className={`text-xs font-semibold mb-1 ${
                                                message.sender === 'tutor' 
                                                    ? 'text-blue-100' 
                                                    : message.sender === 'staff'
                                                    ? 'text-purple-600'
                                                    : 'text-gray-600'
                                            }`}>
                                                {message.senderName}
                                                <span className={`ml-1 text-xs font-normal ${
                                                    message.sender === 'tutor' 
                                                        ? 'text-blue-200' 
                                                        : message.sender === 'staff'
                                                        ? 'text-purple-500'
                                                        : 'text-gray-500'
                                                }`}>
                                                    ({message.sender === 'tutor' ? 'Tutor' : message.sender === 'staff' ? 'Staff' : 'Student'})
                                                </span>
                                                {message.senderTimezone && (message.sender === 'staff' || message.sender === 'student') && (
                                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                                        message.sender === 'staff' 
                                                            ? 'bg-purple-200 text-purple-800' 
                                                            : 'bg-gray-200 text-gray-700'
                                                    }`}>
                                                        {message.senderTimezone}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="whitespace-pre-wrap break-words leading-relaxed">
                                                {message.content}
                                            </div>
                                            <div className={`flex justify-end items-center gap-2 mt-2 ${
                                                message.sender === 'tutor' 
                                                    ? 'text-blue-100' 
                                                    : message.sender === 'staff'
                                                    ? 'text-purple-600'
                                                    : 'text-gray-500'
                                            }`}>
                                                <span className="text-xs">
                                                    {new Date(message.timestamp).toLocaleString(undefined, {
                                                        timeZone: 'UTC',
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })} (UTC)
                                                </span>
                                                {message.sender === 'tutor' && (
                                                    <span className={`text-xs ${
                                                        message.isRead ? 'text-blue-200' : 'text-blue-300'
                                                    }`}>
                                                        {message.isRead ? (
                                                            <>
                                                                Read
                                                                {message.readAt && (
                                                                    <span className="ml-1">
                                                                        {new Date(message.readAt).toLocaleString(undefined, {
                                                                            timeZone: 'UTC',
                                                                            year: 'numeric',
                                                                            month: 'short',
                                                                            day: 'numeric',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit'
                                                                        })} (UTC)
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            'Delivered'
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    
                    {/* Input Area */}
                    <div className="p-5 border-t border-gray-200 bg-white flex-shrink-0">
                        <form onSubmit={onSendMessage} className="flex gap-4 items-end">
                            <textarea
                                value={newMessage}
                                onChange={onMessageChange}
                                onKeyDown={onKeyDown}
                                placeholder="Write a message."
                                disabled={sendingMessage}
                                rows={2}
                                className="flex-1 px-5 py-3 border border-gray-300 rounded-full resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:opacity-70 min-h-[48px]"
                            />
                            <button 
                                type="submit"
                                disabled={sendingMessage || !newMessage.trim()}
                                className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:bg-blue-600 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500 disabled:hover:shadow-none disabled:hover:translate-y-0"
                            >
                                <span className="text-lg">⬆️</span>
                            </button>
                        </form>
                    </div>
                </>
            ) : (
                <div className="flex-1 flex items-center justify-center text-gray-600 text-lg">
                    Select a conversation to start chatting
                </div>
            )}
        </div>
    );
}

export default ChatMessages;
